import { Card } from 'antd';
import { useTranslate } from '@tolgee/react';
import { OrderCardMessage, OrderCardMessageData } from 'components/Dialogue';
import BubbleMessage from 'components/Dialogue/components/Message/BubbleMessage';
import { LocalMessage } from 'hooks/business/conversation';
import { formatNumber } from 'utils/numberFormat';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { tradeList as getTradeList, TradeListParams } from 'services/api/conversation';
import chatMsgRenderFailReport from 'components/Dialogue/components/msgReport';
import { cloneDeep } from 'lodash-es';
import { useQuery, QueryStatus } from '@tanstack/react-query';
import { ApiQueryKey } from 'services/config/queryKey';
import { useInViewport } from 'ahooks';
import { DialogueModeContext } from '../DialogueModeContext.tsx';

type Trade = Conversation.Trade.Vo;

export interface LazyOrderMessageProps {
  storeId: Conversation.Conversation['storeId'];
  externalTradeId: Trade['externalTradeId'];
  externalStoreId: string;
  buyerId: Conversation.Conversation['buyerId'];
  rawMsg: LocalMessage;
  children?: (component: React.ReactNode, status: QueryStatus) => React.ReactNode;
  tunnelAccountId?: string;
  channel?: string;
  quote?: React.ReactNode;
  isUserEnter?: boolean;
  lazyScrollContainer?: Element | null;
  className?: HTMLDivElement['className'];
}

const LazyOrderMessage = (props: LazyOrderMessageProps) => {
  const {
    externalTradeId,
    storeId,
    buyerId,
    children,
    rawMsg,
    tunnelAccountId,
    channel,
    quote,
    externalStoreId,
    isUserEnter,
    lazyScrollContainer,
    className,
  } = props;
  const [content, setContent] = useState<OrderCardMessageData>();
  const { t } = useTranslate();

  const isFaq = useContext(DialogueModeContext) === 'faq';

  const ref = useRef<HTMLDivElement>(null);
  const [inViewPort = false] = useInViewport(ref, {
    root: lazyScrollContainer,
  });

  const queryParams = useMemo(
    () => ({
      internal_buyer_id: buyerId,
      internal_store_id: storeId,
      tunnelAccountId,
      external_trade_ids: channel === 'TOKOPEDIA' ? undefined : externalTradeId,
      invoice_number: channel === 'TOKOPEDIA' ? externalTradeId : undefined,
      page_size: 1,
      page_no: 1,
    }),
    [buyerId, storeId, tunnelAccountId, channel, externalTradeId],
  );
  const {
    status,
    data: trade,
    isError,
  } = useQuery({
    enabled: (inViewPort || isFaq) && !!queryParams,
    staleTime: 3 * 60 * 1000,
    queryKey: [ApiQueryKey.OrderCardTrade, queryParams],
    queryFn: ({ queryKey, signal }) =>
      getTradeList(queryKey[1] as TradeListParams, undefined, signal).then(
        (res) => res.tradeList?.[0],
      ),
  });

  useEffect(() => {
    if (trade) {
      setContent({
        id: trade.externalTradeId,
        invoiceNumber: trade.invoiceNumber,
        internalBuyerId: trade.internalBuyerId,
        externalBuyerId: trade.externalBuyerId,
        createTime: parseInt(trade.createdTimeInMs as unknown as string, 10),
        total: `${trade.unit}  ${formatNumber(trade.payment, '0,0.00')}`,
        items: trade.items.map((it) => {
          return {
            title: it.itemName,
            id: it.itemId,
            amount: it.quantity,
            img: it.itemPic,
            link: it.itemUrl || '',
            externalStoreId,
            spuStockStatus: 'InStock',
          };
        }),
        containsPreOrderItem: trade.containsPreOrderItem,
        tradeStatus: trade.tradeStatus,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trade]);

  useEffect(() => {
    if (isError) {
      chatMsgRenderFailReport('order', cloneDeep(rawMsg));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const component: React.ReactNode = useMemo(() => {
    if (status === 'loading') {
      return <Card loading />;
    }
    if (status === 'success' && content) {
      return (
        <OrderCardMessage
          channel={channel}
          dataSource={content}
          quote={quote}
          isUserEnter={isUserEnter}
          lazyScrollContainer={lazyScrollContainer}
        />
      );
    }
    return (
      <BubbleMessage>
        {t('scene.chatWorkspace.messageThread.lazyCard.error', { type: 'Order' })}
      </BubbleMessage>
    );
  }, [content, status, quote, channel, isUserEnter, lazyScrollContainer, t]);

  return (
    <div className={className} ref={ref}>
      {typeof children === 'function' ? children(component, status) : component}
    </div>
  );
};
export default LazyOrderMessage;
